import { ChangeDetectionStrategy, Component, effect, input, output, signal } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { TableColumnSearchType } from '../models/table-column-search-type.enum';
import { NgStyle } from '@angular/common';
import { TextInputComponent } from '../../form/inputs/text-input/text-input.component';
import { DropdownInputComponent } from '../../form/inputs/dropdown-input/dropdown-input.component';
import { TableColumn } from '../models/table-column';
import { OdataFilterExpression, ODataFilterGroup, SortExpression } from '../../../../core';
import { InputSize } from '../../enum/input-size.enum';
import { DateInputComponent } from '../../form/inputs/date-input/date-input.component';
import { maskitoNumberOptionsGenerator } from '@maskito/kit';
import { MaskitoDirective } from '@maskito/angular';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[lib-table-header-row]',
  imports: [
    TextInputComponent,
    MatIcon,
    ReactiveFormsModule,
    DropdownInputComponent,
    NgStyle,
    DateInputComponent,
    MaskitoDirective,
  ],
  templateUrl: './table-header-row.component.html',
  styleUrl: './table-header-row.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableHeaderRowComponent<T> {
  columns = input.required<TableColumn<T>[]>();
  defaultSortColumn = input.required<TableColumn<T>>();
  defaultSortAsc = input.required<boolean>();
  sortExpression = output<SortExpression | null>();
  filterExpressions = output<(OdataFilterExpression | ODataFilterGroup)[] | null>();

  currentSortColumn = signal<TableColumn<T> | null>(null);
  currentSortAsc = signal<boolean>(false);

  filterSubscriptions: Subscription[] = [];
  protected readonly numericTableFilterMaskitoOptions = maskitoNumberOptionsGenerator({ thousandSeparator: '' });
  protected readonly currencyTableFilterMaskitoOptions = maskitoNumberOptionsGenerator({
    decimalSeparator: '.',
    maximumFractionDigits: 2,
  });

  protected readonly InputSize = InputSize;
  protected readonly TableFilterType = TableColumnSearchType;

  private readonly defaultSortingEffect = effect(() => {
    const defaultSortColumn = this.defaultSortColumn();
    const defaultSortAsc = this.defaultSortAsc();
    if (!defaultSortColumn) return;

    this.currentSortColumn.set(defaultSortColumn);
    this.currentSortAsc.set(defaultSortAsc);
  });

  constructor() {
    effect(() => {
      if (this.currentSortColumn() !== null) {
        this.sortExpression.emit({
          field: this.currentSortColumn()?.sortOdataPropertyName ?? '',
          direction: this.currentSortAsc() ? 'asc' : 'desc',
        });
      } else {
        this.sortExpression.emit(null);
      }
    });

    effect(() => {
      this.setupFilterBindings();
    });
  }

  sortColumnClicked(tableColumn: TableColumn<T>) {
    if (this.currentSortColumn() === tableColumn) {
      this.currentSortAsc.update(s => !s);
    } else {
      this.currentSortAsc.set(true);
    }

    this.currentSortColumn.set(tableColumn);
  }

  private setupFilterBindings(): void {
    this.filterSubscriptions = [];
    this.columns().forEach(column => {
      const sub = column.filterFormControl.valueChanges.subscribe(() => {
        this.buildFilterExpressions();
      });
      this.filterSubscriptions.push(sub);
    });
  }

  private buildFilterExpressions(): void {
    const filterExpressions: (OdataFilterExpression | ODataFilterGroup)[] = [];
    this.columns().forEach(column => {
      const filterExpression = column.buildFilterExpression();
      if (filterExpression) {
        filterExpressions.push(filterExpression);
      }
    });
    this.filterExpressions.emit(filterExpressions);
  }
}
